import React, { useState, useEffect } from 'react';
import { Eye, Table } from 'lucide-react';
import { getApiBaseUrl } from '../config/backend.config';

const PREVIEW_ROWS = 4;

function CsvThumbnail({ file, fileId, workspaceId, getAuthToken, onView }) {
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const cachedUrlRef = React.useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAndParse = async () => {
      if (!workspaceId || !getAuthToken || !file.s3_key) return;

      try {
        const token = await getAuthToken();
        if (!token || cancelled) return;

        const apiBaseUrl = getApiBaseUrl();
        const queryParams = new URLSearchParams({
          s3_key: file.s3_key,
          workspace_id: workspaceId
        }).toString();

        const urlResponse = await fetch(`${apiBaseUrl}/files/presigned-url?${queryParams}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!urlResponse.ok) throw new Error('Failed to get presigned URL');

        const { file_url } = await urlResponse.json();
        if (cancelled) return;
        cachedUrlRef.current = file_url;

        const csvResponse = await fetch(file_url);
        if (!csvResponse.ok) throw new Error('Failed to fetch CSV');

        const content = await csvResponse.text();
        if (cancelled) return;

        const lines = content.split('\n').filter(line => line.trim());
        if (lines.length === 0) {
          setError(true);
          return;
        }

        const parsedHeaders = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        const allRows = lines.slice(1).map(line =>
          line.split(',').map(cell => cell.trim().replace(/"/g, ''))
        );

        setHeaders(parsedHeaders);
        setRows(allRows.slice(0, PREVIEW_ROWS));
        setTotalRows(allRows.length);
        setLoaded(true);
      } catch (err) {
        console.error('Error fetching CSV thumbnail:', err);
        if (!cancelled) setError(true);
      }
    };

    fetchAndParse();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.s3_key, workspaceId]);

  const handleClick = () => {
    if (cachedUrlRef.current) {
      onView(cachedUrlRef.current, file.filename, file.file_type, file.file_size);
    }
  };

  if (error) return null;

  // Cap displayed columns to avoid overflow
  const maxCols = 5;
  const displayHeaders = headers.slice(0, maxCols);
  const hasMoreCols = headers.length > maxCols;

  return (
    <div className="csv-thumbnail-container" onClick={handleClick}>
      {!loaded && (
        <div className="csv-thumbnail-shimmer" />
      )}
      {loaded && (
        <>
          <div className="csv-thumbnail-header">
            <Table size={12} />
            <span className="csv-thumbnail-filename">{file.filename}</span>
            <span className="csv-thumbnail-meta">
              {totalRows} rows · {headers.length} cols
            </span>
          </div>
          <div className="csv-thumbnail-table-wrap">
            <table className="csv-thumbnail-table">
              <thead>
                <tr>
                  {displayHeaders.map((h, i) => (
                    <th key={i}>{h}</th>
                  ))}
                  {hasMoreCols && <th className="csv-thumbnail-more">…</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.slice(0, maxCols).map((cell, ci) => (
                      <td key={ci}>{cell}</td>
                    ))}
                    {hasMoreCols && <td className="csv-thumbnail-more">…</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {totalRows > PREVIEW_ROWS && (
              <div className="csv-thumbnail-fade">
                + {totalRows - PREVIEW_ROWS} more rows
              </div>
            )}
          </div>
          <div className="csv-thumbnail-overlay">
            <Eye size={14} />
            <span>View</span>
          </div>
        </>
      )}
    </div>
  );
}

export default CsvThumbnail;
